<template>
  <v-card
    class="filter-bar"
    :class="{ 'filter-bar--collapsible': collapsible, 'is-open': isOpen }"
    elevation="1"
  >
    <!-- Controles visiveis por padrao. Escondidos atras de um acordeao, nao
         dava para saber se a lista estava filtrada nem por que; no modo
         `collapsible` (dashboard) os campos recolhem, mas o resumo com as
         tags sobe para o topo e continua sempre a vista. -->
    <v-expand-transition>
      <div
        v-show="isOpen"
        id="filter-bar-campos"
        class="filter-bar__fields"
        :class="{ 'filter-bar__fields--balanced': balanced }"
      >
        <div
          v-for="(field, index) in fields"
          :key="field.key"
          class="filter-bar__field"
          :style="{ '--span': balanced ? spans[index] : field.cols || 3 }"
        >
          <label class="filter-bar__label" :for="`filtro-${field.key}`">{{
            field.label
          }}</label>

          <v-select
            v-if="field.type === 'select'"
            :id="`filtro-${field.key}`"
            v-model="draft[field.key]"
            :items="field.items"
            :item-text="field.itemText || 'text'"
            :item-value="field.itemValue || 'value'"
            :placeholder="field.placeholder || 'Todos'"
            solo
            flat
            clearable
            hide-details
            background-color="#F1F3F5"
            class="rounded-lg"
            @change="apply"
          ></v-select>

          <v-autocomplete
            v-else-if="field.type === 'autocomplete'"
            :id="`filtro-${field.key}`"
            v-model="draft[field.key]"
            :items="field.items"
            :item-text="field.itemText || 'text'"
            :item-value="field.itemValue || 'value'"
            :placeholder="field.placeholder || 'Todos'"
            :no-data-text="field.noDataText || 'Nenhum resultado'"
            solo
            flat
            clearable
            hide-details
            background-color="#F1F3F5"
            class="rounded-lg"
            @change="apply"
          ></v-autocomplete>

          <!-- Data: o seletor nativo ja aplica ao escolher, nao ha o que digitar
               e confirmar depois. -->
          <v-text-field
            v-else-if="field.type === 'date'"
            :id="`filtro-${field.key}`"
            v-model="draft[field.key]"
            type="date"
            :max="field.max"
            :min="field.min"
            solo
            flat
            clearable
            hide-details
            background-color="#F1F3F5"
            class="rounded-lg"
            @change="apply"
          ></v-text-field>

          <v-text-field
            v-else
            :id="`filtro-${field.key}`"
            v-model="draft[field.key]"
            :placeholder="field.placeholder"
            solo
            flat
            clearable
            hide-details
            background-color="#F1F3F5"
            class="rounded-lg"
            @keyup.enter="apply"
          ></v-text-field>
        </div>
      </div>
    </v-expand-transition>

    <div class="filter-bar__state">
      <!-- O que a lista esta mostrando e por que, na mesma linha. Cada criterio
           sai sozinho pelo x, que e como se ajusta um filtro na pratica. -->
      <div class="filter-bar__criteria">
        <button
          v-if="collapsible"
          type="button"
          class="filter-bar__toggle"
          :aria-expanded="String(isOpen)"
          aria-controls="filter-bar-campos"
          @click="open = !open"
        >
          <v-icon size="18" color="#9923CD">mdi-tune-variant</v-icon>
          Filtros
          <span v-if="chips.length" class="filter-bar__toggle-count">{{
            chips.length
          }}</span>
          <v-icon size="18" class="filter-bar__chevron"
            >mdi-chevron-down</v-icon
          >
        </button>

        <span class="filter-bar__count">
          <template v-if="loading">Carregando…</template>
          <template v-else
            >{{ count }}
            {{ count === 1 ? unitLabel : unitLabelPlural }}</template
          >
        </span>

        <button
          v-for="chip in chips"
          :key="chip.key"
          type="button"
          class="filter-bar__chip"
          :aria-label="`Remover filtro ${chip.label}: ${chip.value}`"
          @click="remove(chip)"
        >
          <span class="filter-bar__chip-key">{{ chip.label }}</span>
          <span class="filter-bar__chip-value">{{ chip.value }}</span>
          <v-icon size="14" color="#9923CD">mdi-close</v-icon>
        </button>

        <span v-if="!chips.length" class="filter-bar__hint">
          Sem filtros aplicados
        </span>
      </div>

      <div class="filter-bar__actions">
        <v-btn
          v-if="chips.length"
          text
          rounded
          small
          class="filter-bar__clear"
          @click="clear"
          >Limpar tudo</v-btn
        >
        <v-btn
          rounded
          color="secondary-600"
          class="white--text filter-bar__search"
          :loading="loading"
          @click="apply"
        >
          <v-icon left size="18">mdi-magnify</v-icon>Buscar
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script>
import { balancedSpans } from "@/shared/filterGrid";

export default {
  name: "filter-bar",

  props: {
    // [{ key, label, type: 'text'|'select'|'autocomplete', items, placeholder,
    //    cols, itemText, itemValue }]
    fields: {
      type: Array,
      required: true,
    },
    // Filtros efetivamente aplicados na listagem. As tags saem daqui, nao do
    // rascunho: elas descrevem o que esta na tela, nao o que foi digitado.
    value: {
      type: Object,
      required: true,
    },
    // Criterios que a propria pagina controla (ex.: a triagem do plano solo),
    // para que todos os recortes ativos apareçam na mesma linha.
    extraChips: {
      type: Array,
      default: () => [],
    },
    count: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    unitLabel: {
      type: String,
      default: "resultado",
    },
    unitLabelPlural: {
      type: String,
      default: "resultados",
    },
    // Ignora o `cols` de cada campo e reparte os filtros em linhas
    // equilibradas de no maximo 4 na malha de 12 (padrao do dashboard).
    balanced: {
      type: Boolean,
      default: false,
    },
    // Campos recolhiveis, com o resumo (contagem + tags) no topo.
    collapsible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      draft: this.snapshot(this.value),
      open: !this.collapsible,
    };
  },

  computed: {
    isOpen() {
      return !this.collapsible || this.open;
    },
    spans() {
      return balancedSpans(this.fields.length);
    },
    chips() {
      const applied = this.fields
        .filter((field) => this.hasValue(this.value[field.key]))
        .map((field) => ({
          key: field.key,
          label: field.label,
          value: this.displayValue(field, this.value[field.key]),
        }));

      return [...applied, ...this.extraChips];
    },
  },

  watch: {
    // "Limpar" e a remocao de uma tag mexem no objeto do pai; o rascunho tem
    // que acompanhar, senao o campo continua exibindo o valor removido.
    value: {
      deep: true,
      handler(next) {
        this.draft = this.snapshot(next);
      },
    },
  },

  methods: {
    snapshot(source) {
      return this.fields.reduce((acc, field) => {
        acc[field.key] = source[field.key] ?? null;
        return acc;
      }, {});
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== "";
    },
    displayValue(field, value) {
      if (field.type === "text") return value;

      // A tag mostra a data como ela e lida em portugues, nao como o input
      // guarda (ISO).
      if (field.type === "date") {
        const [year, month, day] = String(value).split("-");
        return day ? `${day}/${month}/${year}` : value;
      }

      const items = field.items || [];
      const itemValue = field.itemValue || "value";
      const itemText = field.itemText || "text";
      const match = items.find((item) => item[itemValue] === value);
      return match ? match[itemText] : value;
    },
    apply() {
      const applied = { ...this.draft };
      Object.keys(applied).forEach((key) => {
        if (!this.hasValue(applied[key])) applied[key] = null;
      });
      this.$emit("apply", applied);
    },
    remove(chip) {
      // Tag da propria pagina: quem sabe desfazer e ela.
      if (!Object.prototype.hasOwnProperty.call(this.draft, chip.key)) {
        this.$emit("remove-extra", chip.key);
        return;
      }
      this.draft[chip.key] = null;
      this.apply();
    },
    clear() {
      this.draft = this.fields.reduce((acc, field) => {
        acc[field.key] = null;
        return acc;
      }, {});
      this.$emit("clear");
    },
  },
};
</script>

<style scoped>
.filter-bar {
  border-radius: 10px;
  overflow: hidden;
}

.filter-bar__fields {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  padding: 18px 20px 16px;
}

.filter-bar__field {
  /* --span e a largura em colunas de 12, igual a malha do Vuetify. */
  flex: 1 1 calc(var(--span) / 12 * 100% - 16px);
  min-width: 180px;
}

/* Modo equilibrado: malha de 12 de verdade, sem o min-width que deixava o
   flex quebrar a linha onde quisesse. */
.filter-bar__fields--balanced {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
}

.filter-bar__fields--balanced .filter-bar__field {
  grid-column: span var(--span);
  min-width: 0;
}

@media (max-width: 959px) {
  .filter-bar__fields--balanced .filter-bar__field {
    grid-column: span 6;
  }
}

@media (max-width: 599px) {
  .filter-bar__fields--balanced .filter-bar__field {
    grid-column: span 12;
  }
}

/* Recolhivel: o resumo sobe para o topo e vira o cabecalho. */
.filter-bar--collapsible {
  display: flex;
  flex-direction: column;
}

.filter-bar--collapsible .filter-bar__state {
  order: -1;
  border-top: 0;
  border-bottom: 1px solid transparent;
  padding: 8px 20px 8px 8px;
}

.filter-bar--collapsible.is-open .filter-bar__state {
  border-bottom-color: #ececf2;
}

.filter-bar__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 6px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #3d3745;
  transition: background-color 0.15s ease;
}

.filter-bar__toggle:hover {
  background-color: #f6e9fc;
}

.filter-bar__toggle:focus-visible {
  outline: 2px solid #9923cd;
  outline-offset: 2px;
}

.filter-bar__toggle-count {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background-color: #9923cd;
  color: #fff;
  font-size: 11px;
  line-height: 20px;
  text-align: center;
}

.filter-bar__chevron {
  color: #6b6472 !important;
  transition: transform 0.2s ease;
}

.filter-bar--collapsible.is-open .filter-bar__chevron {
  transform: rotate(180deg);
}

.filter-bar__label {
  display: block;
  font-size: 13px;
  color: #6b6472;
  margin-bottom: 4px;
}

.filter-bar__state {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid #ececf2;
  background-color: #fbfbfd;
}

.filter-bar__criteria {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}

.filter-bar__count {
  font-family: "Poppins", "Sans-serif";
  font-size: 13px;
  font-weight: 600;
  color: #3d3745;
}

.filter-bar__hint {
  font-size: 13px;
  color: #8a8d9f;
}

.filter-bar__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 3px 8px 3px 10px;
  border: 1px solid #e9d2f7;
  border-radius: 999px;
  background-color: #f6e9fd;
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.filter-bar__chip:hover {
  background-color: #efdbfb;
  border-color: #d9b2f0;
}

.filter-bar__chip:focus-visible {
  outline: 2px solid #9923cd;
  outline-offset: 2px;
}

.filter-bar__chip-key {
  color: #8a5aa8;
}

.filter-bar__chip-value {
  font-weight: 600;
  color: #6b1d90;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-bar__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.filter-bar__clear {
  text-transform: none;
  letter-spacing: 0;
  color: #8a8d9f;
}

.filter-bar__search {
  text-transform: none;
  letter-spacing: 0;
}

@media (max-width: 599px) {
  .filter-bar__field {
    flex-basis: 100%;
  }

  .filter-bar__state {
    align-items: flex-start;
    flex-direction: column;
  }

  .filter-bar__actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (prefers-reduced-motion: reduce) {
  .filter-bar__chip,
  .filter-bar__toggle,
  .filter-bar__chevron {
    transition: none;
  }
}
</style>
